import type {
  BulkUpdateDocumentGroupItem,
  DocumentGroup,
} from "services/document-group-api";
import type { DocumentGroupTag } from "services/document-group-tag-api";

import { METADOC_GROUP_ID_PREFIX } from "../../MetadocConstants";
import type { TocEntry } from "../../metadoc.types";
import type { PendingDocumentGroupChanges } from "./useLazyDocumentGroupUpdates";

/**
 * Convert the in-memory pending map into bulk_update API items.
 *
 * Only keys explicitly present on each pending change are included so the
 * backend can tell "field not requested" from "field cleared to null".
 */
export const pendingChangesToBulkItems = (
  pendingChanges: PendingDocumentGroupChanges,
): BulkUpdateDocumentGroupItem[] =>
  Array.from(pendingChanges.entries()).map(([id, change]) => ({
    id,
    ...(change.document_type !== undefined
      ? { document_type: change.document_type }
      : {}),
    ...("date" in change ? { date: change.date } : {}),
    ...("provider" in change ? { provider: change.provider } : {}),
    ...("facility" in change ? { facility: change.facility } : {}),
    ...(change.delete ? { delete: true } : {}),
  }));

export const buildTocEntriesWithPending = (
  documentGroups: DocumentGroup[],
  documentGroupTags: DocumentGroupTag[],
  pendingChanges: PendingDocumentGroupChanges,
): TocEntry[] => {
  const tagsByDocumentGroupId = new Map<number, DocumentGroupTag[]>();
  for (const tag of documentGroupTags) {
    const groupTags = tagsByDocumentGroupId.get(tag.document_group) ?? [];
    groupTags.push(tag);
    tagsByDocumentGroupId.set(tag.document_group, groupTags);
  }

  return documentGroups.map((group) => {
    const pending = pendingChanges.get(group.id);
    const pendingProvider =
      pending !== undefined && "provider" in pending
        ? (pending.provider ?? undefined)
        : undefined;

    return {
      id: `${METADOC_GROUP_ID_PREFIX}${group.id}`,
      startPage: group.start_pg,
      endPage: group.end_pg,
      category: pending?.document_type ?? group.document_type,
      provider: pendingProvider ?? group.provider ?? undefined,
      dateOfService:
        pending !== undefined && "date" in pending
          ? (pending.date ?? undefined)
          : group.date || undefined,
      documentGroupId: group.id,
      isCloned: group.is_cloned ?? false,
      hasPendingChange: pending !== undefined,
      isPendingDelete: pending?.delete === true,
      documentGroupTags: tagsByDocumentGroupId.get(group.id),
    };
  });
};

/** Merge pending facility edits into the base facility lookup used by TOC filters. */
export const mergePendingFacilityByDocumentGroupId = (
  baseFacilityByDocumentGroupId: Map<number, string>,
  pendingChanges: PendingDocumentGroupChanges,
): Map<number, string> => {
  const merged = new Map(baseFacilityByDocumentGroupId);
  for (const [groupId, change] of pendingChanges) {
    if ("facility" in change) {
      merged.set(groupId, change.facility ?? "");
    }
  }
  return merged;
};
